import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  ShoppingCart, Trash2, Percent, Lock, Unlock, Receipt, Search, X, Printer,
  AlertTriangle,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import ModalConfirmar from '../ui/ModalConfirmar';
import Motivo from '../experiencia/Motivo';
import TicketVenta from '../ui/TicketVenta';
import NotaExplicativa from '../ui/NotaExplicativa';
import EstadoVacio from '../ui/EstadoVacio';
import Modal from '../ui/Modal';
import { useEscanerTeclado } from '../lib/useEscanerTeclado';
import { useAvisos } from '../lib/contextoAvisos';
import { Cargando } from '../ui/EstadoDePantalla';
import CampoContrasena from '../ui/CampoContrasena';
import { ContextoAuth } from '../lib/contextoAuth';
import {
  borrarBorradorVenta, guardarBorradorVenta, leerBorradorVenta, textoVentaRecuperada,
} from '../lib/borradorVenta';
import { useCambiosSinGuardar } from '../ui/navegacionReact';
// DIRECTRIZ-PWA F2 (11 oct 2026, DISENO-F2 §4.7 y §4.8; parche del
// integrador para erp-comercial): la caja sin señal.
import { leerConCopia, horaDe } from '../lib/conCopia';
import { leerTrabajo, guardarTrabajo, sirveLaCopia } from '../lib/cacheTrabajo';
import { useEnLinea } from '../lib/useEnLinea';
import { importesDeVenta, faltaTratoFiscal, montoDeDescuento } from '../lib/importesVenta';
import {
  registrarVenta, carritoDeVentaGuardada, descartarVenta, EVENTO_VENTA_ENVIADA,
} from '../lib/colaVentas';
import AvisoCobroSinSenal from '../integrador/pwa/AvisoCobroSinSenal';
import Ayuda from '../ui/Ayuda';

const RUTA_CATALOGO = '/productos?limite=500';
const sinAcentos = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

// Buscar sobre la copia guardada: por SKU, código de barras y nombre, sin
// acentos ni mayúsculas, ocho resultados, el SKU exacto primero.
function buscarEnCopia(lista, consulta) {
  const q = sinAcentos(consulta).trim();
  if (!q || !Array.isArray(lista)) return [];
  const encontrados = lista.filter((p) => p?.activo !== false && (
    sinAcentos(p.sku).includes(q) || sinAcentos(p.codigo_barras).includes(q) || sinAcentos(p.nombre).includes(q)));
  return exactoPrimero(encontrados, consulta).slice(0, 8);
}

const MOTIVOS = [
  ['CLIENTE_FRECUENTE', 'Cliente frecuente'],
  ['PRODUCTO_PROXIMO_A_CADUCAR', 'Producto próximo a caducar'],
  ['DANO_EMPAQUE', 'Daño en el empaque'],
  ['AUTORIZACION_GERENCIA', 'Autorización de gerencia'],
  ['PROMOCION', 'Promoción'],
  ['OTRO', 'Otro (explicar)'],
];

const METODOS = [
  ['EFECTIVO', 'Efectivo'],
  ['TARJETA', 'Tarjeta'],
  ['TRANSFERENCIA', 'Transferencia'],
];

const dinero = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
    .format(Number(n || 0));

// Mostrador. Está pensada para usarse con las manos ocupadas y un cliente
// enfrente: el foco vuelve siempre al buscador, un escáner USB funciona sin
// configurar nada (emula un teclado y termina con Enter), y el cobro está a
// una tecla de distancia.
// UN PEDIDO B2C ENTRA AQUI, no a un cobro aparte. El descuento atomico,
// la idempotencia y el turno de caja ya viven en esta pantalla y llevan
// meses rodados: un segundo mostrador para cobrar pedidos habria sido
// duplicar el sitio donde se mueve el dinero.
// EL PEDIDO, CON LO QUE HAY AHORA AL LADO DE LO QUE PIDIO EL CLIENTE.
//
// Es la pantalla de CA-K5, y su razon de ser cabe en una frase: descubrir
// que faltan tres balatas A MITAD DEL COBRO, con el cliente enfrente y
// gente detras, es la peor forma de enterarse. Aqui se ve antes, renglon
// por renglon y con el numero — «quedan 2 de las 5»— para no tener que
// ponerse a contar delante de la fila.
function AvisoPedido({ pedido }) {
  const faltantes = pedido.renglones.filter((r) => Number(r.falta) > 0);
  const cambios = pedido.renglones.filter((r) => r.cambio_precio);

  if (pedido.motivo_no_cobrable) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-xl p-3 space-y-1">
        <div className="font-semibold text-red-900 text-sm">
          Pedido {pedido.folio} — no se puede cobrar
        </div>
        {/* EL MOTIVO, no un «no válido». Se le va a leer en voz alta a
            alguien que está enfrente esperando. */}
        <p className="text-sm text-red-800">{pedido.motivo_no_cobrable}</p>
      </div>
    );
  }

  return (
    <div className={`border rounded-xl p-3 space-y-2 ${
      faltantes.length ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-sm text-gray-900">
          Pedido {pedido.folio}
        </span>
        {pedido.nombre_cliente && (
          <span className="text-sm text-gray-700">· {pedido.nombre_cliente}</span>
        )}
        {pedido.telefono_cliente && (
          <span className="text-xs text-gray-500">{pedido.telefono_cliente}</span>
        )}
      </div>

      {faltantes.length === 0 ? (
        <p className="text-sm text-green-900">
          Está completo: hay de todo lo que pidió. Cobra normal.
        </p>
      ) : (
        <div className="space-y-1">
          <p className="text-sm text-amber-900 font-medium">
            Falta mercancía. Ya ajusté el carrito a lo que sí hay:
          </p>
          <ul className="text-sm text-amber-900 space-y-0.5">
            {faltantes.map((r) => (
              <li key={r.sku}>
                · <strong>{r.nombre_visto}</strong>: pidió {Number(r.cantidad)},
                {' '}quedan {Number(r.disponible_ahora)} — faltan {Number(r.falta)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {cambios.length > 0 && (
        <div className="space-y-1 pt-1 border-t border-amber-200/60">
          {/* CAMBIAR EL PRECIO EN SILENCIO ES LO QUE NO SE PUEDE HACER. Se
              cobra el de ahora —es el bueno— y se dice cuál vio el cliente,
              para que en el mostrador puedan explicarlo. */}
          <p className="text-sm text-amber-900 font-medium">
            Cambió de precio desde que lo armó:
          </p>
          <ul className="text-sm text-amber-900 space-y-0.5">
            {cambios.map((r) => (
              <li key={r.sku}>
                · <strong>{r.nombre_visto}</strong>: vio {dinero(r.precio_visto)},
                {' '}ahora {dinero(r.precio_ahora)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// Si lo escrito es exactamente el SKU de uno de los resultados, ése va
// primero: es el que agrega el Enter del escáner. El servidor ordena por
// SKU, y «SKU-001» también encuentra «SKU-0010».
function exactoPrimero(lista, consulta) {
  if (!Array.isArray(lista)) return [];
  const q = String(consulta || '').trim().toUpperCase();
  const i = lista.findIndex((p) => String(p?.sku || '').toUpperCase() === q);
  return i > 0 ? [lista[i], ...lista.slice(0, i), ...lista.slice(i + 1)] : lista;
}

export default function PuntoDeVenta({ contexto, irA }) {
  const avisos = useAvisos();
  const enLinea = useEnLinea();
  const usuario = useContext(ContextoAuth)?.usuario || null;
  const idUsuario = usuario?.id;
  // De dónde salió la búsqueda: null = del servidor; {momento} = de la copia;
  // 'sin-copia' = sin señal y sin catálogo guardado.
  const [copiaBusqueda, setCopiaBusqueda] = useState(null);
  // La venta guardada sin señal que se está cobrando de nuevo («Abrir en
  // Punto de venta» desde Pendientes de enviar): se descarta al cobrar.
  const [reemplaza, setReemplaza] = useState(null);
  const [turno, setTurno] = useState(null);
  const [cargandoTurno, setCargandoTurno] = useState(true);
  const [config, setConfig] = useState(null);
  const [carrito, setCarrito] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [ubicacion, setUbicacion] = useState('');
  const [ubicaciones, setUbicaciones] = useState([]);
  // Saldo por ubicación de lo que hay en el carrito (T-11). `null` = no
  // calculado todavía o carrito vacío; un objeto = ya se consultó.
  const [saldos, setSaldos] = useState(null);
  // Si quien atiende YA eligió una ubicación a mano, no se le vuelve a
  // mover sola: la propuesta automática es una ayuda, no una imposición.
  const ubicacionTocada = useRef(false);
  const [error, setError] = useState(null);
  const [cobrando, setCobrando] = useState(false);
  const [descuentoEn, setDescuentoEn] = useState(null);  // índice o 'TOTAL'
  const [descuentoTotal, setDescuentoTotal] = useState(null);
  const [ticket, setTicket] = useState(null);
  // El pedido que se esta cobrando, si se llego aqui escaneando un ORD-.
  const [pedido, setPedido] = useState(null);
  const [pedidoError, setPedidoError] = useState(null);
  const buscadorRef = useRef(null);

  // El turno con copia: sin señal se usa el último turno conocido de esta
  // sesión. Un 404 («no hay turno») borra la copia para no reabrir un turno
  // que ya se cerró.
  const cargarTurno = useCallback(
    () => leerConCopia(idUsuario, '/ventas/turnos/actual')
      .then(({ datos }) => setTurno(datos || null))
      .catch((e) => {
        if (e?.status === 404) guardarTrabajo(idUsuario, '/ventas/turnos/actual', null);
        setTurno(null);
      }),
    [idUsuario],
  );

  useEffect(() => {
    Promise.all([
      cargarTurno(),
      leerConCopia(idUsuario, '/ventas/configuracion').then(({ datos }) => setConfig(datos)).catch(() => {}),
      leerConCopia(idUsuario, '/ubicaciones').then(({ datos: u }) => {
        setUbicaciones(u);
        // La vitrina es de donde se despacha en un mostrador; si no hay,
        // la primera ubicación con existencia.
        const vitrina = u.find((x) => x.tipo === 'VITRINA')
          || u.find((x) => Number(x.unidades) > 0) || u[0];
        if (vitrina) setUbicacion(vitrina.codigo);
      }).catch(() => {}),
    ]).finally(() => setCargandoTurno(false));
    // El catálogo para buscar sin señal, en segundo plano y después de
    // pintar: si falla no pasa nada, la búsqueda en vivo sigue (R-1: ≈ 0.4 MB
    // por 500 productos, medido; cabe entero).
    if (navigator.onLine) {
      api.get(RUTA_CATALOGO).then((lista) => guardarTrabajo(idUsuario, RUTA_CATALOGO, lista)).catch(() => {});
    }
  }, [cargarTurno, idUsuario]);

  // «Abrir en Punto de venta» desde Pendientes de enviar: el carrito se arma
  // desde la venta guardada, se cobra de nuevo con señal (otra clave) y la
  // vieja se descarta al confirmar.
  useEffect(() => {
    const clave = contexto?.carrito_local;
    if (!clave) return undefined;
    let vivo = true;
    carritoDeVentaGuardada(clave).then((v) => {
      if (!vivo || !v) return;
      setCarrito(v.carrito);
      setDescuentoTotal(v.descuentoTotal);
      if (v.ubicacion) { ubicacionTocada.current = true; setUbicacion(v.ubicacion); }
      setReemplaza(clave);
      if (v.motivo) setError(`Esta venta se guardó sin señal y el servidor no la aceptó: ${v.motivo} Revisa el carrito y cóbrala de nuevo.`);
    });
    return () => { vivo = false; };
  }, [contexto?.carrito_local]);

  // Cuando una venta guardada sin señal se envía y recibe folio, el ticket
  // abierto con esa clave pasa a decir «Venta 1234».
  useEffect(() => {
    const oir = (ev) => {
      const d = ev.detail || {};
      setTicket((t) => (t?.pendiente && t.clave_idempotencia === d.clave_idempotencia && d.venta ? d.venta : t));
    };
    window.addEventListener(EVENTO_VENTA_ENVIADA, oir);
    return () => window.removeEventListener(EVENTO_VENTA_ENVIADA, oir);
  }, []);

  // Búsqueda con espera: cada pulsación no puede ser una consulta.
  //
  // El vaciado va DENTRO del temporizador y no en el cuerpo del efecto: un
  // setState sincrono ahí encadena renders y React lo señala con razón.
  const consulta = busqueda.trim();
  useEffect(() => {
    const t = setTimeout(() => {
      if (consulta.length < 2) { setSugerencias([]); setCopiaBusqueda(null); return; }
      // Sin señal —o si la búsqueda en vivo no llega— se busca sobre la
      // copia guardada y se dice de cuándo es (DISENO-F2 §4.7).
      const desdeCopia = () => leerTrabajo(idUsuario, RUTA_CATALOGO).then((copia) => {
        if (!copia) { setSugerencias([]); setCopiaBusqueda('sin-copia'); return; }
        setSugerencias(buscarEnCopia(copia.datos, consulta));
        setCopiaBusqueda({ momento: copia.momento });
      });
      if (!navigator.onLine) { desdeCopia(); return; }
      // EL PARÁMETRO SE LLAMA `buscar` (routers/productos.py, `listar`).
      // Hasta el 29 sep 2026 aquí decía `busqueda`, que el servidor no
      // conoce y descarta: la caja recibía los ocho primeros productos por
      // orden alfabético fuera cual fuera lo escrito, y el Enter del
      // escáner agregaba el primero de esos — otro producto. En la pila
      // del piloto era uno recién dado de alta sin precio, y el cobro
      // acababa en «no tiene precio de venta» (LISTO b, P8).
      api.get(`/productos?buscar=${encodeURIComponent(consulta)}&limite=8`)
        .then((lista) => { setSugerencias(exactoPrimero(lista, consulta)); setCopiaBusqueda(null); })
        .catch((e) => (sirveLaCopia(e) ? desdeCopia() : setSugerencias([])));
    }, consulta.length < 2 ? 0 : 250);
    return () => clearTimeout(t);
  }, [consulta, idUsuario]);

  // DE QUE LOTE VA A SALIR, Y SI ESTA A PUNTO DE CADUCAR.
  //
  // Desde N-13 la caja aplica FEFO y elige el lote sola. Eso es lo que se
  // quería, pero lo hace en SILENCIO: si el lote que despacha caduca
  // pasado mañana, quien atiende no se entera y la automatización vende sin
  // darle a nadie la ocasión de rebajarlo —existe el motivo de descuento
  // «producto próximo a caducar»— o de retirarlo.
  //
  // `para_venta` hace que la respuesta sea lo que la caja VA A HACER, no lo
  // que dice la política a secas: sin él anunciaría lotes caducados, que la
  // venta después se niega a despachar.
  useEffect(() => {
    if (!ubicacion || carrito.length === 0) return undefined;
    let vigente = true;
    const sinConsultar = carrito.filter((r) => r.lote === undefined);
    if (sinConsultar.length === 0) return undefined;

    Promise.all(sinConsultar.map((r) =>
      api.get(`/inventario/sugerencia-lote?sku=${encodeURIComponent(r.sku)}`
              + `&ubicacion=${encodeURIComponent(ubicacion)}`
              + `&cantidad=${r.cantidad}&para_venta=true`)
        .then((d) => ({ sku: r.sku, lote: d.lotes[0] || null, alerta: d.alerta_caducidad }))
        // Un fallo aquí NO puede estorbar el cobro: se marca como
        // consultado sin dato y la venta sigue su curso.
        .catch(() => ({ sku: r.sku, lote: null, alerta: false }))))
      .then((consultas) => {
        if (!vigente) return;
        const por = Object.fromEntries(consultas.map((c) => [c.sku, c]));
        setCarrito((c) => c.map((x) => (por[x.sku]
          ? { ...x, lote: por[x.sku].lote, alertaCaducidad: por[x.sku].alerta }
          : x)));
      });
    return () => { vigente = false; };
  }, [carrito, ubicacion]);

  // T-11 · DE DÓNDE HAY EXISTENCIA DE LO QUE SE VA A VENDER.
  //
  // El selector "Sale de" ofrecía TODAS las ubicaciones y arrancaba en la
  // primera del abecedario, que casi nunca es donde está la mercancía. Para
  // una venta lo que importa es dónde HAY: se consulta el saldo de cada
  // producto del carrito, se suma por ubicación, y la de mayor saldo queda
  // propuesta. Un fallo aquí NO estorba el cobro: se cae al comportamiento
  // de antes (la lista completa, con su preselección de siempre).
  //
  // Se recalcula cuando cambia el CONJUNTO de productos, no cada vez que se
  // ajusta una cantidad: la existencia por ubicación no depende de cuántas
  // piezas se van a vender.
  const clavesSaldo = [...new Set(carrito.map((r) => r.sku))].sort().join('|');
  useEffect(() => {
    const skus = clavesSaldo ? clavesSaldo.split('|') : [];
    let vigente = true;
    // Todo `setState` va DENTRO del `.then`, nunca en el cuerpo del efecto:
    // un `setState` sincrono encadena renders y React lo señala con razón
    // (misma regla que la búsqueda con espera de más arriba). Con el
    // carrito vacío el saldo es `null` y el selector vuelve a su lista
    // completa de siempre.
    // Sin señal no se consulta: `saldos` queda en null y cada renglón dice
    // «Existencia sin comprobar (sin señal)» en vez de fingir un cero.
    const consulta = skus.length === 0 || !navigator.onLine
      ? Promise.resolve(null)
      : Promise.all(skus.map((sku) =>
        api.get(`/inventario/existencias?sku=${encodeURIComponent(sku)}`
                + '&solo_con_stock=true&limite=1000')
          .catch(() => [])))
        .then((listas) => {
          const por = {};
          for (const filas of listas) {
            for (const f of (filas || [])) {
              por[f.ubicacion] = (por[f.ubicacion] || 0) + Number(f.cantidad || 0);
            }
          }
          return por;
        });
    consulta.then((por) => {
      if (!vigente) return;
      setSaldos(por);
      if (!por) return;
      // La de mayor saldo queda propuesta, salvo que ya se haya elegido una
      // a mano.
      const mejor = Object.entries(por).sort((a, b) => b[1] - a[1])[0];
      if (mejor && mejor[1] > 0 && !ubicacionTocada.current) {
        setUbicacion(mejor[0]);
        setCarrito((c) => c.map((x) => ({ ...x, lote: undefined })));
      }
    });
    return () => { vigente = false; };
  }, [clavesSaldo]);

  // LA VENTA EN CURSO SE GUARDA EN ESTE DISPOSITIVO · TC-DR-AT (escenario 3).
  //
  // Salir de esta pantalla —atrás, el menú, una recarga— vaciaba el
  // carrito. Ahora cada cambio se guarda por persona y por turno
  // (`lib/borradorVenta.js`) y se recupera al volver a entrar. Se limpia al
  // cobrar y al vaciar el carrito. La venta que llega de un pedido no se
  // guarda ni pisa lo guardado: su carrito lo arma el pedido.
  //
  // `restaurado` evita que el guardado corra ANTES de leer: sin él, el
  // carrito vacío del primer render borraría el borrador que se viene a
  // recuperar.
  const restaurado = useRef(false);
  const idTurno = turno?.id ?? null;
  const dePedido = !!contexto?.folio_pedido;
  useEffect(() => {
    if (cargandoTurno || restaurado.current) return undefined;
    let vigente = true;
    // El `setState` va dentro del `.then`, como en el resto de la pantalla.
    Promise.resolve().then(() => {
      if (!vigente || restaurado.current) return;
      restaurado.current = true;
      if (dePedido) return;
      const b = leerBorradorVenta(usuario, idTurno);
      if (!b) return;
      setCarrito((c) => (c.length ? c : b.carrito));
      setDescuentoTotal((d) => d || b.descuentoTotal);
      if (b.ubicacionElegida && ubicaciones.some((u) => u.codigo === b.ubicacion)) {
        ubicacionTocada.current = true;
        setUbicacion(b.ubicacion);
      }
      avisos.exito(textoVentaRecuperada(b.carrito.length));
    });
    return () => { vigente = false; };
  }, [cargandoTurno, dePedido, usuario, idTurno, ubicaciones, avisos]);

  useEffect(() => {
    if (!restaurado.current || dePedido) return;
    if (carrito.length === 0) { borrarBorradorVenta(usuario); return; }
    guardarBorradorVenta(usuario, {
      turno: idTurno, carrito, descuentoTotal, ubicacion,
      ubicacionElegida: ubicacionTocada.current,
    });
  }, [carrito, descuentoTotal, ubicacion, usuario, idTurno, dePedido]);

  // Con productos en el carrito, atrás y el menú preguntan antes de salir.
  useCambiosSinGuardar(() => carrito.length > 0 && !ticket, {
    detalle: 'La venta en curso queda guardada en este dispositivo. Vuelve a Punto de venta para continuarla.',
  });

  // EL PEDIDO SE CARGA CON LO QUE HAY AHORA, no con lo que habia cuando el
  // cliente lo armo. El servidor devuelve, renglon por renglon, cuanto
  // queda y si el precio cambio; eso es lo que se pinta arriba. Descubrir
  // que falta algo A MITAD DEL COBRO, con el cliente enfrente y fila
  // detras, es exactamente lo que esta pantalla existe para evitar.
  useEffect(() => {
    const folio = contexto?.folio_pedido;
    if (!folio) return undefined;
    let vivo = true;
    api.get(`/pedidos/${encodeURIComponent(folio)}`)
      .then((p) => {
        if (!vivo) return;
        setPedido(p);
        if (p.motivo_no_cobrable) return;   // no se prellena lo que no se puede cobrar
        setCarrito(p.renglones
          .filter((r) => r.vendible_ahora)
          .map((r) => ({
            sku: r.sku,
            nombre: r.nombre_visto,
            // LO QUE SE PUEDE SURTIR, no lo que se pidio. Poner la cantidad
            // pedida cuando no alcanza obligaria a corregir a mano delante
            // del cliente, o a que el cobro fallara al final.
            cantidad: Math.min(Number(r.cantidad), Number(r.disponible_ahora)),
            // EL PRECIO ES EL DE AHORA. El que vio el cliente se guarda
            // para poder decirle que cambio, no para cobrarselo.
            precio: Number(r.precio_ahora ?? r.precio_visto ?? 0),
            // R-EC1 · misma advertencia venga por escaneo o por pedido: una
            // línea sin precio lleva su aviso pegado.
            sinPrecio: !(Number(r.precio_ahora ?? r.precio_visto) > 0),
            descuento: null,
          }))
          .filter((r) => r.cantidad > 0));
      })
      .catch((e) => vivo && setPedidoError(
        !navigator.onLine || sirveLaCopia(e)
          ? 'Sin señal no se puede comprobar el pedido. Cóbralo cuando vuelva la señal.'
          : (e instanceof ErrorApi ? e.message : 'No se pudo abrir el pedido.')));
    return () => { vivo = false; };
  }, [contexto?.folio_pedido]);

  function agregar(producto, unidades = 1) {
    setError(null);
    // R-EC1 · SE AVISA AL AGREGAR, no al cobrar. La resolución del escaneo
    // trae `precio_venta`, que puede venir sin capturar. Un renglón sin
    // precio entra en $0 y el cobro lo niega AL FINAL, con el cliente
    // enfrente y la fila detrás. Se avisa aquí, en el momento, y la línea
    // se agrega igual —con la advertencia pegada— para seguir escaneando el
    // resto: el aviso no bloquea, solo adelanta lo que se sabría después.
    const sinPrecio = !(Number(producto.precio_venta) > 0);
    if (sinPrecio) {
      avisos.aviso(`${producto.sku} no tiene precio de venta. `
        + 'Ponle uno en su ficha para poder cobrarlo.');
    }
    setCarrito((c) => {
      const i = c.findIndex((x) => x.sku === producto.sku);
      if (i >= 0) {
        return c.map((x, j) => (j === i
          // `lote: undefined` vuelve a preguntar de dónde sale: con otra
          // cantidad puede repartirse entre lotes distintos.
          ? { ...x, cantidad: x.cantidad + unidades, lote: undefined } : x));
      }
      return [...c, {
        sku: producto.sku,
        nombre: producto.nombre,
        cantidad: unidades,
        precio: Number(producto.precio_venta || 0),
        sinPrecio,
        // Cómo se grava (S-1): el ticket sin señal calcula el IVA por
        // producto, como el servidor. Viene en la copia del catálogo.
        trato_iva: producto.trato_iva || null,
        descuento: null,
      }];
    });
    setBusqueda('');
    setSugerencias([]);
    buscadorRef.current?.focus();
  }

  // Un lector USB o Bluetooth teclea el código sin que nadie haya hecho
  // clic en la caja de búsqueda. Se resuelve por el mismo camino que el
  // escáner de cámara, así que un EAN de fábrica funciona igual que un QR
  // de la casa. Se apaga con un modal abierto: ahí las teclas son de quien
  // está escribiendo un descuento.
  useEscanerTeclado(
    (codigo) => {
      api.get(`/qr/resolver?codigo=${encodeURIComponent(codigo)}`)
        .then((r) => {
          if (r.tipo !== 'PRODUCTO') {
            avisos.aviso(`El código ${codigo} no corresponde a ningún producto.`);
            return;
          }
          const unidades = Number(r.presentacion?.unidades || 1);
          agregar({ ...r.datos, precio_venta: r.datos.precio_venta }, unidades);
        })
        .catch((e) => avisos.error(e.message));
    },
    { activo: !cobrando && descuentoEn === null && !ticket },
  );


  // T-11 · Ubicaciones partidas por si tienen saldo de lo que se va a
  // vender. Las que tienen, ordenadas por saldo (mayor primero).
  const conExistencia = saldos
    ? ubicaciones
      .filter((u) => Number(saldos[u.codigo]) > 0)
      .sort((a, b) => Number(saldos[b.codigo]) - Number(saldos[a.codigo]))
    : [];
  const sinExistencia = saldos
    ? ubicaciones.filter((u) => !(Number(saldos[u.codigo]) > 0))
    : ubicaciones;

  // La MISMA cuenta que el servidor y que el ticket sin señal (P1-1 de
  // AUDITORIA-F2): cada descuento se redondea en su renglón, en centavos
  // exactos, y el del total se calcula sobre lo que queda tras ellos.
  const importes = importesDeVenta(
    carrito.map((r) => ({
      precio_unitario: r.precio, cantidad: r.cantidad, descuento: r.descuento, trato_iva: r.trato_iva,
    })),
    descuentoTotal, config || {},
  );
  const { bruto } = importes;
  const descuentos = importes.descuento_total;
  const total = Math.max(0, importes.cobrado);

  async function cobrar(metodo, efectivo) {
    if (!carrito.length) throw new ErrorApi('No hay nada en el carrito.');
    if (!ubicacion) throw new ErrorApi('Elige de qué ubicación sale la mercancía.');

    const cuerpo = {
      renglones: carrito.map((r) => ({
        sku: r.sku,
        cantidad: r.cantidad,
        ubicacion,
        precio_unitario: String(r.precio),
        ...(r.descuento ? { descuento: r.descuento } : {}),
      })),
      metodo_pago: metodo,
      ...(descuentoTotal ? { descuento: descuentoTotal } : {}),
      ...(metodo === 'EFECTIVO' && efectivo ? { efectivo_recibido: String(efectivo) } : {}),
      // La clave de idempotencia la pone la cola (`colaVentas`), UNA vez y
      // antes del primer intento: un doble clic en Cobrar o un reenvío sin
      // señal no pueden cobrar dos veces (DISENO-F2 D-3).
      // Y SI ESTO CIERRA UN PEDIDO, va en la MISMA peticion: cobrar y
      // cerrarlo ocurren en una sola transaccion en el servidor. Mandarlo
      // en dos pasos dejaria una ventana con la venta hecha y el pedido
      // aun pendiente — y el siguiente que escaneara ese codigo lo
      // cobraria otra vez.
      ...(pedido && !pedido.motivo_no_cobrable ? { folio_pedido: pedido.folio } : {}),
    };
    const r = await registrarVenta(idUsuario, cuerpo, {
      sinSenal: !navigator.onLine,
      cobrarSinSenal: config?.cobrar_sin_senal === true,
      nombres: Object.fromEntries(carrito.map((x) => [x.sku, x.nombre])),
      config: config || {},
      tratos: Object.fromEntries(carrito.filter((x) => x.trato_iva).map((x) => [x.sku, x.trato_iva])),
      total,
      cajero: usuario?.nombre || null,
    });
    if (reemplaza) { await descartarVenta(reemplaza).catch(() => {}); setReemplaza(null); }
    if (r.estado === 'encolado') {
      avisos.exito(`Venta guardada en este teléfono por ${dinero(r.ventaLocal.total)}. Se enviará sola al volver la señal; el folio lo da TEGO entonces.`);
      setTicket(r.ventaLocal);
      setCarrito([]);
      setPedido(null);
      setDescuentoTotal(null);
      borrarBorradorVenta(usuario);
      buscadorRef.current?.focus();
      return;
    }
    const { venta } = r;
    avisos.exito(`Venta ${venta.folio} cobrada por ${dinero(venta.total)}.${r.aviso ? ` ${r.aviso}` : ''}`);
    setTicket(venta);
    setCarrito([]);
    setPedido(null);
    setDescuentoTotal(null);
    await cargarTurno();
    buscadorRef.current?.focus();
  }

  // Los candados de la caja sin señal, en el orden en que se evalúan
  // (DISENO-F2 §4.8). Cada uno deja el botón gris CON su motivo a la vista.
  const sinAjuste = !enLinea && config?.cobrar_sin_senal !== true;
  const pedidoSinSenal = !enLinea && !!pedido && !pedido.motivo_no_cobrable;
  const sinTrato = !enLinea ? faltaTratoFiscal(carrito) : null;
  const { excedido } = importes;
  const motivoCobrar = !carrito.length
    ? 'Escanea o busca un producto para empezar el ticket; entonces se podrá cobrar.'
    : excedido
      ? `El descuento ${excedido.renglon === 'TOTAL' ? 'al total' : `en ${carrito[excedido.renglon]?.sku}`} (${dinero(excedido.descuento)}) es mayor que su importe (${dinero(excedido.importe)}). Cámbialo o quítalo para poder cobrar.`
    : sinAjuste
      ? 'Sin señal no se puede cobrar: tu empresa no tiene encendido «Cobrar sin señal». Pídeselo a quien administra TEGO (menú Operación → Cobrar sin señal).'
      : pedidoSinSenal
        ? 'Este pedido solo se puede cobrar con señal: hay que comprobar que sigue vigente.'
        : sinTrato
          ? `Sin señal no se puede cobrar ${sinTrato.sku}: este teléfono no tiene guardado cómo se grava (IVA) y el ticket saldría con otro total. Cóbralo cuando vuelva la señal.`
          : '';

  if (cargandoTurno) return <Cargando filas={4} />;

  const exigeTurno = config?.exige_turno !== false;
  if (exigeTurno && !turno) {
    return <SinTurno onAbierto={(t) => setTurno(t)} enLinea={enLinea} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Punto de venta</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Cada venta descuenta del inventario en el momento de cobrarla.
          </p>
        </div>
        {turno && <BarraTurno turno={turno} onCerrado={() => setTurno(null)} />}
      </div>

      {pedidoError && (
        <div className="border border-red-200 bg-red-50 text-red-800 rounded-xl p-3 text-sm">
          {pedidoError}
        </div>
      )}
      {pedido && <AvisoPedido pedido={pedido} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Buscador y carrito */}
        <div className="lg:col-span-2 space-y-3">
          <div className="relative">
            <Search className="h-4 w-4 text-gray-500 absolute left-3 top-3.5" />
            <input
              ref={buscadorRef}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => {
                // Un escáner USB teclea el código y manda Enter. Si hay una
                // sola coincidencia, se agrega sola: en el mostrador, elegir
                // de una lista de uno es un paso de más.
                if (e.key === 'Enter' && sugerencias.length >= 1) {
                  agregar(sugerencias[0]);
                }
              }}
              placeholder="Escanea o escribe el código / nombre…"
              autoFocus
              className="w-full pl-9 pr-3 py-3 text-base border border-gray-300 rounded-xl bg-white focus:border-[var(--color-marca)] focus:ring-1 focus:ring-[var(--color-marca)] outline-none"
            />
            {copiaBusqueda && copiaBusqueda !== 'sin-copia' && consulta.length >= 2 && (
              <p role="status" className="mt-1 text-xs text-amber-800">
                <strong>Buscando en la copia guardada a las {horaDe(copiaBusqueda.momento)}.</strong> Precios y existencias pueden haber cambiado.
              </p>
            )}
            {copiaBusqueda === 'sin-copia' && consulta.length >= 2 && (
              <p role="status" className="mt-1 text-xs text-amber-800">
                Sin señal y el catálogo no está guardado en este teléfono. Abre el punto de venta una vez con señal para poder buscar sin ella.
              </p>
            )}
            {sugerencias.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                {sugerencias.map((p) => (
                  <button
                    key={p.sku}
                    onClick={() => agregar(p)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-3"
                  >
                    <span>
                      <span className="font-medium text-gray-900">{p.nombre}</span>
                      <span className="block text-xs font-mono text-gray-500">{p.sku}</span>
                    </span>
                    <span className="text-sm font-semibold text-gray-800">
                      {dinero(p.precio_venta)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {carrito.length === 0 ? (
              <EstadoVacio
                icono={ShoppingCart}
                titulo="El carrito está vacío"
                descripcion="Escanea un código de barras o busca el producto por nombre."
              />
            ) : (
              /* LOOP MÓVIL F4 · M1/M3: el carrito se desplaza dentro de su
                 contenedor, no la página, si un nombre largo no cabe. */
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {carrito.map((r, i) => (
                    <tr key={r.sku} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{r.nombre}</div>
                        <div className="text-xs font-mono text-gray-500">{r.sku}</div>
                        {r.sinPrecio && (
                          <div className="text-xs text-amber-700 mt-0.5 flex items-center gap-1 flex-wrap">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            <span>No tiene precio de venta. No se podrá cobrar hasta ponérselo.</span>
                            <button
                              type="button"
                              onClick={() => irA?.('productos', { sku: r.sku })}
                              className="font-semibold underline hover:text-amber-800"
                            >
                              Ponle precio en su ficha →
                            </button>
                          </div>
                        )}
                        {saldos === null && !enLinea && (
                          <div className="text-xs text-amber-700 mt-0.5">Existencia sin comprobar (sin señal)</div>
                        )}
                        {r.lote && (
                          <div className={`text-xs mt-0.5 ${
                            r.alertaCaducidad ? 'font-semibold text-amber-700' : 'text-gray-500'}`}>
                            Lote {r.lote.codigo_lote}
                            {r.lote.dias_para_caducar != null && (
                              r.lote.dias_para_caducar <= 0
                                ? ' · caduca HOY'
                                : ` · caduca en ${r.lote.dias_para_caducar} día(s)`
                            )}
                          </div>
                        )}
                        {r.descuento && (
                          <div className="text-xs text-amber-700 mt-0.5">
                            −{r.descuento.tipo === 'PORCENTAJE'
                              ? `${r.descuento.valor}%`
                              : dinero(r.descuento.valor)} · {r.descuento.motivo_tipo}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 w-24">
                        <input
                          type="number" min="1" step="1" value={r.cantidad}
                          onChange={(e) => setCarrito((c) => c.map((x, j) =>
                            // `lote: undefined` vuelve a preguntar: con otra
                            // cantidad puede repartirse entre lotes distintos.
                            (j === i ? { ...x, cantidad: Math.max(1, Number(e.target.value)), lote: undefined } : x)))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-right tabular-nums"
                        />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-gray-600 w-28">
                        {dinero(r.precio)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-semibold w-28">
                        {dinero(importes.renglones[i]?.importe ?? 0)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap w-24">
                        <button onClick={() => setDescuentoEn(i)}
                                title="Descuento"
                                className="text-gray-500 hover:text-amber-700 mr-2">
                          <Percent className="h-4 w-4" />
                        </button>
                        <button onClick={() => setCarrito((c) => c.filter((_, j) => j !== i))}
                                title="Quitar"
                                className="text-gray-500 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </div>
          )}
        </div>

        {/* Totales y cobro */}
        <div className="space-y-3">
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <label className="block">
              <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Sale de
              </span>
              <select value={ubicacion}
                      onChange={(e) => {
                        ubicacionTocada.current = true;
                        setUbicacion(e.target.value);
                        // Otra ubicación, otros lotes: lo consultado deja
                        // de valer y hay que volver a preguntarlo todo.
                        setCarrito((c) => c.map((x) => ({ ...x, lote: undefined })));
                      }}
                      className="entrada">
                {/* T-11 · Cuando ya se sabe dónde HAY, las ubicaciones con
                    existencia van arriba y ordenadas por saldo; las demás se
                    dejan al alcance, por si hace falta forzar una. Sin ese
                    dato (carrito vacío o consulta caída), la lista completa
                    de siempre. */}
                {saldos && conExistencia.length > 0 ? (
                  <>
                    <optgroup label="Con existencia de lo que vas a vender">
                      {conExistencia.map((u) => (
                        <option key={u.id ?? u.codigo} value={u.codigo}>
                          {u.codigo} — {u.nombre} · {saldos[u.codigo]} disponibles
                        </option>
                      ))}
                    </optgroup>
                    {sinExistencia.length > 0 && (
                      <optgroup label="Otras ubicaciones (sin existencia de esto)">
                        {sinExistencia.map((u) => (
                          <option key={u.id ?? u.codigo} value={u.codigo}>
                            {u.codigo} — {u.nombre}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </>
                ) : (
                  ubicaciones.map((u) => (
                    <option key={u.id ?? u.codigo} value={u.codigo}>{u.codigo} — {u.nombre}</option>
                  ))
                )}
              </select>
              {saldos && carrito.length > 0 && conExistencia.length === 0 && (
                <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>
                    Ningún sitio tiene existencia de estos productos. Revisa el
                    inventario o recíbelos antes de venderlos.
                  </span>
                </p>
              )}
            </label>

            <Linea etiqueta="Importe" valor={dinero(bruto)} />
            {descuentos > 0 && (
              <Linea etiqueta="Descuentos" valor={`−${dinero(descuentos)}`} tono="amber" />
            )}
            <div className="border-t border-gray-200 pt-2 flex items-baseline justify-between">
              <span className="text-sm font-semibold text-gray-700">Total</span>
              <span className="text-2xl font-semibold text-gray-900 tabular-nums">
                {dinero(total)}
              </span>
            </div>
            {config?.precios_incluyen_impuesto && Number(config?.tasa_impuesto) > 0 && (
              <p className="text-xs text-gray-500">
                IVA incluido ({(Number(config.tasa_impuesto) * 100).toFixed(0)}%). El
                ticket lo desglosa.
              </p>
            )}
          </div>

          <button
            onClick={() => setCobrando(true)}
            disabled={!!motivoCobrar}
            aria-describedby={motivoCobrar ? 'pos-cobrar-falta' : undefined}
            className="w-full bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-40 text-white font-semibold py-3 rounded-xl text-lg"
          >
            Cobrar {dinero(total)}
          </button>
          <Motivo id="pos-cobrar-falta" cuando={!!motivoCobrar} className="text-center">
            {motivoCobrar}
          </Motivo>
          {sinAjuste && carrito.length > 0 && <Ayuda id="pwa.pos.cobrar_sin_senal" />}

          {carrito.length > 0 && (
            descuentoTotal ? (
              <button onClick={() => setDescuentoTotal(null)}
                      className="w-full text-sm font-semibold text-amber-700 py-2">
                Quitar el descuento del total
                (−{descuentoTotal.tipo === 'PORCENTAJE'
                  ? `${descuentoTotal.valor}%` : dinero(descuentoTotal.valor)})
              </button>
            ) : (
              <button onClick={() => setDescuentoEn('TOTAL')}
                      className="w-full text-sm font-semibold text-[var(--color-marca)] py-2">
                Aplicar descuento al total
              </button>
            )
          )}
        </div>
      </div>

      {cobrando && (
        <ModalCobro
          total={total}
          sinSenal={!enLinea}
          onCobrar={cobrar}
          onCerrar={() => setCobrando(false)}
        />
      )}

      {descuentoEn !== null && (
        <ModalDescuento
          enLinea={enLinea}
          renglon={descuentoEn === 'TOTAL' ? null : carrito[descuentoEn]}
          base={descuentoEn === 'TOTAL'
            ? importes.bruto - importes.descuento_renglones
            : importes.renglones[descuentoEn]?.importe_bruto ?? 0}
          onAplicar={(d) => {
            if (descuentoEn === 'TOTAL') {
              setDescuentoTotal(d);
            } else {
              setCarrito((c) => c.map((x, i) => (i === descuentoEn ? { ...x, descuento: d } : x)));
            }
            setDescuentoEn(null);
          }}
          onCerrar={() => setDescuentoEn(null)}
        />
      )}

      {ticket && <Ticket venta={ticket} onCerrar={() => setTicket(null)} />}
    </div>
  );
}

function Linea({ etiqueta, valor, tono }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500">{etiqueta}</span>
      <span className={`tabular-nums ${tono === 'amber' ? 'text-amber-700' : 'text-gray-800'}`}>
        {valor}
      </span>
    </div>
  );
}

// ------------------------------------------------------------------ turno
function SinTurno({ onAbierto, enLinea = true }) {
  const [fondo, setFondo] = useState('0');
  const [error, setError] = useState(null);

  return (
    <div className="max-w-md mx-auto mt-10 space-y-4">
      <NotaExplicativa titulo="Antes de vender, abre tu turno">
        El turno es lo que hace que el dinero cuadre al final del día: se abre
        con el <strong>fondo de caja</strong>, se cierra contando lo que hay, y
        la diferencia queda con tu nombre. Sin turno, un faltante no tiene a
        quién atribuirse.
      </NotaExplicativa>

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <label className="block">
          <span className="block text-sm font-semibold text-gray-700 mb-1">
            Fondo de caja con el que empiezas
          </span>
          <input value={fondo} onChange={(e) => setFondo(e.target.value)}
                 inputMode="decimal" className="entrada text-lg tabular-nums" />
        </label>
        {error && <p className="text-sm text-red-700">{error}</p>}
        <button
          onClick={() => api.post('/ventas/turnos/abrir', { fondo_inicial: fondo })
            .then(onAbierto).catch((e) => setError(e.message))}
          disabled={!enLinea}
          aria-describedby={!enLinea ? 'pos-turno-sin-senal' : undefined}
          className="w-full bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-40 text-white font-semibold py-2.5 rounded-lg inline-flex items-center justify-center gap-2"
        >
          <Unlock className="h-4 w-4" /> Abrir turno
        </button>
        <Motivo id="pos-turno-sin-senal" cuando={!enLinea} className="text-center">
          Sin señal no se puede abrir el turno: hace falta señal una vez para abrirlo.
        </Motivo>
      </div>
    </div>
  );
}

function BarraTurno({ turno, onCerrado }) {
  const [cerrando, setCerrando] = useState(false);
  return (
    <>
      <div className="flex items-center gap-3 text-sm bg-white border border-gray-200 rounded-xl px-3 py-2">
        <span className="text-gray-500">
          Turno abierto · <strong className="text-gray-800">{turno.ventas}</strong> venta(s)
        </span>
        <span className="text-gray-500">|</span>
        <span className="text-gray-500">
          En caja <strong className="text-gray-800 tabular-nums">
            {dinero(turno.efectivo_esperado)}
          </strong>
        </span>
        <button onClick={() => setCerrando(true)}
                className="inline-flex items-center gap-1 font-semibold text-[var(--color-marca)]">
          <Lock className="h-3.5 w-3.5" /> Cerrar
        </button>
      </div>

      {cerrando && <ModalCierre turno={turno} onCerrado={onCerrado}
                                onCerrar={() => setCerrando(false)} />}
    </>
  );
}

function ModalCierre({ turno, onCerrado, onCerrar }) {
  const [contado, setContado] = useState('');
  const [resultado, setResultado] = useState(null);

  const diferencia = contado === '' ? null
    : Number(contado) - Number(turno.efectivo_esperado);

  return (
    <ModalConfirmar
      titulo="Cerrar turno"
      descripcion="Cuenta el efectivo del cajón y escribe cuánto hay. El sistema compara después; no al revés."
      extra={
        <div className="space-y-3">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
            <Linea etiqueta="Fondo inicial" valor={dinero(turno.fondo_inicial)} />
            <Linea etiqueta="Ventas en efectivo" valor={dinero(turno.efectivo_ventas)} />
            {Number(turno.otros_metodos) > 0 && (
              <Linea etiqueta="Otros métodos (no en caja)" valor={dinero(turno.otros_metodos)} />
            )}
          </div>
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">
              Efectivo contado
            </span>
            <input value={contado} onChange={(e) => setContado(e.target.value)}
                   inputMode="decimal" autoFocus
                   className="entrada text-lg tabular-nums" />
          </label>
          {/* La diferencia se enseña solo DESPUÉS de teclear lo contado: al
              revés invitaría a "cuadrar" escribiendo lo esperado. */}
          {diferencia !== null && (
            <div className={`text-sm rounded-lg p-2.5 border ${
              diferencia === 0
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}>
              {diferencia === 0
                ? 'Cuadra exacto.'
                : `${diferencia > 0 ? 'Sobran' : 'Faltan'} ${dinero(Math.abs(diferencia))}. Quedará registrado con tu nombre.`}
            </div>
          )}
          {resultado && (
            <p className="text-sm text-gray-600">Turno cerrado.</p>
          )}
        </div>
      }
      etiquetaConfirmar="Cerrar turno"
      onConfirmar={async () => {
        if (contado === '') throw new ErrorApi('Escribe cuánto efectivo contaste.');
        const r = await api.post('/ventas/turnos/cerrar', { efectivo_contado: contado });
        setResultado(r);
        onCerrado();
      }}
      onCerrar={onCerrar}
    />
  );
}

// ------------------------------------------------------------------ cobro
function ModalCobro({ total, sinSenal = false, onCobrar, onCerrar }) {
  const [metodo, setMetodo] = useState('EFECTIVO');
  const [efectivo, setEfectivo] = useState('');

  const cambio = metodo === 'EFECTIVO' && efectivo !== ''
    ? Number(efectivo) - Number(total) : null;

  return (
    <ModalConfirmar
      titulo={`Cobrar ${dinero(total)}`}
      extra={
        <div className="space-y-3">
          {sinSenal && <AvisoCobroSinSenal />}
          <div className="grid grid-cols-3 gap-2">
            {METODOS.map(([v, etiqueta]) => (
              <button key={v} onClick={() => setMetodo(v)}
                      disabled={sinSenal && v !== 'EFECTIVO'}
                      aria-describedby={sinSenal && v !== 'EFECTIVO' ? 'pos-metodo-sin-senal' : undefined}
                      className={`py-2 rounded-lg text-sm font-semibold border disabled:opacity-40 ${
                        metodo === v
                          ? 'bg-[var(--color-marca)] text-white border-[var(--color-marca)]'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}>
                {etiqueta}
              </button>
            ))}
          </div>
          <Motivo id="pos-metodo-sin-senal" cuando={sinSenal}>
            Sin señal solo se cobra en efectivo: la tarjeta necesita la confirmación del banco en línea.
          </Motivo>

          {metodo === 'EFECTIVO' && (
            <>
              <label className="block">
                <span className="block text-sm font-semibold text-gray-700 mb-1">
                  Con cuánto paga
                </span>
                <input value={efectivo} onChange={(e) => setEfectivo(e.target.value)}
                       inputMode="decimal" autoFocus
                       className="entrada text-lg tabular-nums" />
              </label>
              {cambio !== null && cambio >= 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <div className="text-xs font-semibold text-green-800 uppercase">Cambio</div>
                  <div className="text-3xl font-semibold text-green-900 tabular-nums">
                    {dinero(cambio)}
                  </div>
                </div>
              )}
              {cambio !== null && cambio < 0 && (
                <p className="text-sm text-red-700">
                  Faltan {dinero(Math.abs(cambio))}.
                </p>
              )}
            </>
          )}
        </div>
      }
      etiquetaConfirmar="Confirmar cobro"
      onConfirmar={() => onCobrar(metodo, efectivo || null)}
      onCerrar={onCerrar}
    />
  );
}

function ModalDescuento({ renglon, base = 0, onAplicar, onCerrar, enLinea = true }) {
  const [d, setD] = useState({
    tipo: 'PORCENTAJE', valor: '', motivo_tipo: 'CLIENTE_FRECUENTE',
    motivo: '', pin_autorizacion: '', id_autorizador: '',
  });
  const set = (k) => (e) => setD((s) => ({ ...s, [k]: e.target.value }));
  // AHORA SE PIDE QUIÉN, no solo el PIN. Antes el PIN se comparaba contra
  // todos los encargados: bastaba acertar el de cualquiera, y el descuento
  // quedaba firmado por aquel cuyo PIN coincidiera — la venta decía
  // «autorizó Ana» sin que Ana hubiera estado en el mostrador.
  const [gente, setGente] = useState([]);

  useEffect(() => {
    let vivo = true;
    api.get('/ventas/autorizadores')
      .then((g) => vivo && setGente(g))
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  // S-3: validación en vivo; un descuento no puede pasar de su importe.
  const monto = Number(d.valor) > 0 ? montoDeDescuento({ tipo: d.tipo, valor: d.valor }, base) : 0;
  const excede = monto > base;
  const motivoExcede = excede
    ? `El descuento (${dinero(monto)}) es mayor que el importe (${dinero(base)}). Escribe uno de hasta ${dinero(base)}.`
    : '';

  return (
    <ModalConfirmar
      titulo={renglon ? `Descuento en ${renglon.sku}` : 'Descuento al total'}
      bloqueo={motivoExcede}
      descripcion="Todo descuento queda registrado con quién lo aplicó, quién lo autorizó y por qué."
      extra={
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">Tipo</span>
              <select value={d.tipo} onChange={set('tipo')} className="entrada">
                <option value="PORCENTAJE">Porcentaje</option>
                <option value="MONTO">Monto fijo</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">
                {d.tipo === 'PORCENTAJE' ? 'Porcentaje' : 'Pesos'}
              </span>
              <input value={d.valor} onChange={set('valor')} inputMode="decimal"
                     autoFocus className="entrada tabular-nums"
                     aria-invalid={excede || undefined}
                     aria-describedby={excede ? 'pos-descuento-excede' : undefined} />
            </label>
          </div>
          <Motivo id="pos-descuento-excede" cuando={excede}>{motivoExcede}</Motivo>
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">Motivo</span>
            <select value={d.motivo_tipo} onChange={set('motivo_tipo')} className="entrada">
              {MOTIVOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </label>
          {d.motivo_tipo === 'OTRO' && (
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">
                Explica cuál
              </span>
              <input value={d.motivo} onChange={set('motivo')} className="entrada" />
            </label>
          )}
          {/* Los dos campos van juntos y en este orden: primero a quién
              se le pide y luego que esa persona teclee lo suyo. Un PIN sin
              nombre no vale, y el servidor lo rechaza. */}
          <div className="rounded-lg border border-gray-200 p-2.5 space-y-2">
            <span className="block text-sm font-semibold text-gray-700">
              Autorización (si el descuento pasa de tu límite)
            </span>
            <label className="block">
              <span className="block text-xs text-gray-500 mb-1">¿Quién autoriza?</span>
              <select value={d.id_autorizador} onChange={set('id_autorizador')}
                      className="entrada">
                <option value="">— Nadie: mi nivel alcanza —</option>
                {gente.map((g) => (
                  <option key={g.id} value={g.id}>{g.nombre}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-gray-500 mb-1">
                Su PIN, tecleado por esa persona
              </span>
              <CampoContrasena value={d.pin_autorizacion} onChange={set('pin_autorizacion')}
                               maxLength={4} inputMode="numeric"
                               autoComplete="off" disabled={!enLinea}
                               aria-describedby={!enLinea ? 'pos-pin-sin-senal' : undefined}
                               className="entrada font-mono tracking-[0.4em] w-44" />
            </label>
            <Motivo id="pos-pin-sin-senal" cuando={!enLinea}>
              El PIN de autorización se comprueba en línea.
            </Motivo>
            <span className="block text-xs text-gray-500">
              El descuento queda firmado con ese nombre, así que tiene que
              teclearlo quien autoriza.
            </span>
          </div>
        </div>
      }
      etiquetaConfirmar="Aplicar descuento"
      onConfirmar={() => {
        if (!d.valor || Number(d.valor) <= 0) {
          throw new ErrorApi('Escribe cuánto se descuenta.');
        }
        if (excede) throw new ErrorApi(motivoExcede);
        // Se avisa aquí antes de que el servidor lo rechace: en un
        // mostrador con cola, un viaje de ida y vuelta para decir «falta
        // el nombre» es medio minuto delante del cliente.
        if (d.pin_autorizacion && !d.id_autorizador) {
          throw new ErrorApi('Elige quién autoriza: el PIN solo no basta.');
        }
        if (d.id_autorizador && !d.pin_autorizacion) {
          throw new ErrorApi(`Falta el PIN de quien autoriza.`);
        }
        onAplicar({
          tipo: d.tipo,
          valor: Number(d.valor),
          motivo_tipo: d.motivo_tipo,
          ...(d.motivo ? { motivo: d.motivo } : {}),
          ...(d.pin_autorizacion ? { pin_autorizacion: d.pin_autorizacion } : {}),
          ...(d.id_autorizador ? { id_autorizador: Number(d.id_autorizador) } : {}),
        });
      }}
      onCerrar={onCerrar}
    />
  );
}

// Un producto, una línea.
//
// Desde que el mostrador aplica FEFO, una venta de 8 puede salir de dos
// lotes distintos y el servidor devuelve DOS renglones — que es lo
// correcto para el almacén y para la trazabilidad. Pero en el papel que se
// le da al cliente, ver dos veces el mismo producto sin explicación parece
// un cobro duplicado, y es lo primero que le van a reclamar al cajero.
//
// De qué lote salió cada cosa se ve en la ficha de la venta (Tickets), que
// es donde importa.

function Ticket({ venta, onCerrar }) {
  return (
    <Modal
      titulo={venta.pendiente ? 'Venta pendiente de enviar' : `Venta ${venta.folio}`}
      icono={Receipt}
      ancho="max-w-sm"
      onCerrar={onCerrar}
    >
      <div className="space-y-3">
        {/* El mismo papel que se imprime es el que se ve. Antes esta
            pantalla armaba su propia lista y la de Ventas otra: dos
            tickets distintos de la misma venta, y ninguno imprimible. */}
        <div className="border border-gray-200 rounded-lg p-3">
          <TicketVenta venta={venta} />
        </div>

        <div className="sin-imprimir grid grid-cols-2 gap-2">
          <button type="button" onClick={() => window.print()}
                  className="inline-flex items-center justify-center gap-1.5 border
                             border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg
                             hover:bg-gray-50 focus:outline-none focus:ring-2
                             focus:ring-[var(--color-marca)]">
            <Printer className="h-4 w-4" aria-hidden="true" /> Imprimir
          </button>
          <button type="button" onClick={onCerrar}
                  className="bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)]
                             text-white font-semibold py-2.5 rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]">
            Siguiente venta
          </button>
        </div>
      </div>
    </Modal>
  );
}
